/**
 * B2BConstants — Référentiels et primitives UI partagés du module SADA B2B.
 *
 * Tout ce qui est PUR (mapping label, classes Tailwind, helpers d'affichage,
 * petits composants sans état) est centralisé ici pour que les sous-vues
 * (OrderDetail, OrdersList, CreateOrderForm, MiniDashboard, ClientFeedback)
 * partagent une seule source de vérité, alignée sur le backend `routers/b2b.py`.
 *
 * Toute évolution des référentiels DOIT être faite en miroir côté backend.
 */
import React from 'react';
import {
  Building2, Phone, MessageCircle, Mail, User, AlertCircle,
} from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL + '/api';
export const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem('dermascan_token')}`,
});

export const CHANNEL_ICONS = {
  portail_b2b: Building2,
  telephone: Phone,
  whatsapp: MessageCircle,
  passage_physique: User,
  email: Mail,
  autre: AlertCircle,
};
export const CHANNEL_LABELS = {
  portail_b2b: 'Portail B2B',
  telephone: 'Téléphone',
  whatsapp: 'WhatsApp',
  passage_physique: 'Passage physique',
  email: 'Email',
  autre: 'Autre',
};
export const MODE_LABELS = {
  livraison: 'Livraison',
  retrait_pharmacie: 'Retrait pharmacie',
};

// Couleurs de statut groupées par phase métier.
export const STATUS_TONE = {
  demande_recue: 'bg-blue-100 text-blue-800 border-blue-200',
  en_cours_cotation: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  cotation_envoyee: 'bg-purple-100 text-purple-800 border-purple-200',
  quoted_validated: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  modification_requested: 'bg-amber-100 text-amber-900 border-amber-300',
  cotation_rejected: 'bg-red-100 text-red-900 border-red-300',
  cotation_refused_final: 'bg-red-200 text-red-900 border-red-400',
  a_relancer: 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300',
  nouvelle_cotation_a_preparer: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  en_attente_validation: 'bg-amber-100 text-amber-800 border-amber-200',
  validee: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  refusee: 'bg-red-100 text-red-800 border-red-200',
  en_preparation: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  prete: 'bg-teal-100 text-teal-800 border-teal-200',
  livree: 'bg-emerald-200 text-emerald-900 border-emerald-300',
  retiree: 'bg-emerald-200 text-emerald-900 border-emerald-300',
  cloturee: 'bg-zinc-200 text-zinc-700 border-zinc-300',
  annulee: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};
export const PAYMENT_TONE = {
  non_facture: 'bg-zinc-100 text-zinc-600',
  facture: 'bg-blue-100 text-blue-700',
  en_attente_paiement: 'bg-amber-100 text-amber-700',
  partiellement_paye: 'bg-orange-100 text-orange-700',
  paye: 'bg-emerald-100 text-emerald-700',
  impaye_a_relancer: 'bg-red-100 text-red-700',
};

// ─── Phase A++ : libellés des motifs et actions client ────────────────────
// Doivent rester strictement alignés avec le backend (cf. b2b.py REVISION_REASONS,
// REFUSAL_REASONS, LINE_ACTION_TYPES) — toute évolution se fait des deux côtés.
export const REVISION_REASON_LABELS = {
  price_global_too_high: 'Montant global trop élevé',
  price_some_items: 'Certains produits trop chers',
  qty_to_reduce: 'Quantités à réduire',
  substitution: 'Demande de substitution',
  not_needed: 'Produit non souhaité',
  need_clarification: 'Besoin de précision',
  other: 'Autre motif',
};
export const REFUSAL_REASON_LABELS = {
  price_global_too_high: 'Trop cher globalement',
  no_budget: 'Budget non disponible',
  order_cancelled: 'Commande annulée',
  bought_elsewhere: 'Achat effectué ailleurs',
  postponed: 'Besoin reporté',
  other: 'Autre',
};
export const LINE_ACTION_LABELS = {
  keep: 'Accepter telle quelle',
  reduce_qty: 'Réduire la quantité',
  substitute: 'Demander un équivalent',
  remove: 'Supprimer cette ligne',
};
export const LINE_ACTION_TONES = {
  keep: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  reduce_qty: 'bg-amber-50 text-amber-800 border-amber-200',
  substitute: 'bg-blue-50 text-blue-800 border-blue-200',
  remove: 'bg-red-50 text-red-800 border-red-200',
};

export const DELAY_PRESETS = [
  '48h après validation',
  '72h après validation',
  '5 jours ouvrés',
  '10 à 15 jours',
  'Commande internationale',
  'À confirmer',
];

export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export const StatusBadge = ({ id, label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_TONE[id] || 'bg-zinc-100 text-zinc-700'}`}>
    {label}
  </span>
);

export const PaymentBadge = ({ id, label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${PAYMENT_TONE[id] || 'bg-zinc-100 text-zinc-700'}`}>
    {label}
  </span>
);
